import { inject, Injectable, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthApi } from '@doctor-scheduling/web/data-access';
import { User } from '@doctor-scheduling/shared/models';
import {
  Observable,
  tap,
  catchError,
  of,
  throwError,
  switchMap,
  map,
  finalize,
  shareReplay,
  timeout,
} from 'rxjs';

/** Options controlling how aggressively a session is cleared. */
interface ClearSessionOptions {
  /** When false, keep the refresh token in localStorage for later restore. */
  removeRefresh?: boolean;
  /** When false, do not navigate to the login route. */
  navigate?: boolean;
}

/** Root auth state: session tokens, user profile, and login actions. */
@Injectable({
  providedIn: 'root',
})
export class AuthStore {
  /** Auth API client for login, register, and session endpoints. */
  private readonly authApi = inject(AuthApi);
  /** Router used to redirect after auth state changes. */
  private readonly router = inject(Router);

  /** Current authenticated user, or null when logged out. */
  readonly currentUser = signal<User | null>(null);
  /** Short-lived JWT access token for API requests. */
  readonly accessToken = signal<string | null>(null);
  /** True while a login, register, or session-restore request is in flight. */
  readonly isLoading = signal(false);
  /**
   * True after startup session restore has finished (success or failure).
   * Guards must wait for this before reading `isAuthenticated`.
   */
  readonly isInitialized = signal(false);

  /** Whether a user session is currently active. */
  readonly isAuthenticated = computed(() => !!this.currentUser());
  /** Role of the current user, or null when logged out. */
  readonly userRole = computed(() => this.currentUser()?.role ?? null);
  /** Clinic id scoped to the current user, or null when logged out. */
  readonly clinicId = computed(() => this.currentUser()?.clinicId ?? null);
  /** Linked doctor profile id when the user is a DOCTOR, otherwise null. */
  readonly doctorId = computed(() => this.currentUser()?.doctor?.id ?? null);
  /** True when the user is a clinic or platform admin. */
  readonly isClinicAdmin = computed(() => {
    const role = this.userRole();
    return role === 'CLINIC_ADMIN' || role === 'SUPER_ADMIN';
  });
  /** True when the user is a platform super admin. */
  readonly isPlatformAdmin = computed(() => this.userRole() === 'SUPER_ADMIN');
  /** True when the user is a doctor. */
  readonly isDoctor = computed(() => this.userRole() === 'DOCTOR');

  /** Shared in-flight refresh so concurrent 401s do not rotate the token twice. */
  private refreshInFlight: Observable<boolean> | null = null;

  constructor() {
    this.restoreAccessTokenFromSession();
    this.init();
  }

  /**
   * Authenticates with email/username and password, then navigates to the dashboard.
   * @param credentials - Login identifier and password hash
   * @returns Observable of the login API response
   */
  login(credentials: { identifier: string; passwordHash: string }): Observable<unknown> {
    this.isLoading.set(true);
    return this.authApi.login(credentials).pipe(
      tap((res) => {
        this.isLoading.set(false);
        if (res.success && res.data) {
          this.setSession(res.data);
          this.router.navigate(['/dashboard']);
        }
      }),
      catchError((err) => {
        this.isLoading.set(false);
        return throwError(() => err);
      })
    );
  }

  /**
   * Registers a clinic admin, stores the session, and navigates to the dashboard.
   * @param data - Registration payload including clinic and user details
   * @returns Observable of the register API response
   */
  register(data: {
    clinicName: string;
    email: string;
    username: string;
    passwordHash: string;
    firstName: string;
    lastName: string;
  }): Observable<unknown> {
    this.isLoading.set(true);
    return this.authApi.register(data).pipe(
      tap((res) => {
        this.isLoading.set(false);
        if (res.success && res.data) {
          this.setSession(res.data);
          this.router.navigate(['/dashboard']);
        }
      }),
      catchError((err) => {
        this.isLoading.set(false);
        return throwError(() => err);
      })
    );
  }

  /** Calls the logout API and clears local session state. */
  logout(): void {
    this.authApi.logout().subscribe({
      next: () => this.clearSession({ removeRefresh: true, navigate: true }),
      error: () => this.clearSession({ removeRefresh: true, navigate: true }),
    });
  }

  /**
   * Ensures a usable session exists (refresh + profile) for route guards after API blips.
   * @returns Observable that emits true when authenticated
   */
  ensureSession(): Observable<boolean> {
    if (this.currentUser() && this.accessToken()) {
      return of(true);
    }
    const refresh = localStorage.getItem('refresh_token');
    if (!refresh) {
      return of(false);
    }
    return this.refreshToken().pipe(
      switchMap((ok) => {
        if (!ok) return of(false);
        return this.fetchMe().pipe(
          map((user) => {
            if (user) {
              this.currentUser.set(user);
              return true;
            }
            return false;
          })
        );
      })
    );
  }

  /**
   * Refreshes the access token using the stored refresh token.
   * Network / API-down failures keep the refresh token so the user is not force-logged-out
   * while the Nest server is restarting after a code change.
   * @returns Observable that emits true when refresh succeeds, false otherwise
   */
  refreshToken(): Observable<boolean> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    const refresh = localStorage.getItem('refresh_token');
    if (!refresh) {
      this.clearSession({ removeRefresh: false, navigate: false });
      return of(false);
    }

    this.refreshInFlight = this.authApi.refreshToken(refresh).pipe(
      map((res) => {
        if (res.success && res.data?.accessToken) {
          this.persistAccessToken(res.data.accessToken);
          if (res.data.refreshToken) {
            localStorage.setItem('refresh_token', res.data.refreshToken);
          }
          return true;
        }
        // Explicit API rejection of the refresh token — hard logout
        this.clearSession({ removeRefresh: true, navigate: true });
        return false;
      }),
      catchError((err: unknown) => {
        if (this.isTransientFailure(err)) {
          // API restarting / network blip — keep refresh token, do not redirect
          return of(false);
        }
        this.clearSession({ removeRefresh: true, navigate: true });
        return of(false);
      }),
      finalize(() => {
        this.refreshInFlight = null;
      }),
      shareReplay(1)
    );

    return this.refreshInFlight;
  }

  /**
   * Restores session on startup: refresh first (access token may be in sessionStorage), then load profile.
   */
  private init(): void {
    const refresh = localStorage.getItem('refresh_token');
    if (!refresh) {
      this.isInitialized.set(true);
      return;
    }

    this.isLoading.set(true);

    // If we still have a warm access token (HMR / soft reload), try getMe first
    const warmToken = this.accessToken();
    const bootstrap$: Observable<User | null> = warmToken
      ? this.authApi.getMe().pipe(
          map((res) => (res.success && res.data ? res.data : null)),
          catchError(() =>
            this.refreshToken().pipe(
              switchMap((ok) => (ok ? this.fetchMe() : of(null)))
            )
          )
        )
      : this.refreshToken().pipe(
          switchMap((ok) => (ok ? this.fetchMe() : of(null)))
        );

    bootstrap$
      .pipe(
        // Avoid a blank app shell when the API is unreachable / hung (e.g. port conflict).
        timeout({ first: 10_000 }),
        catchError(() => of(null)),
        finalize(() => {
          this.isLoading.set(false);
          this.isInitialized.set(true);
        })
      )
      .subscribe((user) => {
        if (user) {
          this.currentUser.set(user);
        }
        // Do not clear refresh token here on failure — API may have been temporarily down
      });
  }

  /**
   * Loads the current user profile.
   * @returns Observable of the user or null
   */
  private fetchMe(): Observable<User | null> {
    return this.authApi.getMe().pipe(
      map((res) => (res.success && res.data ? res.data : null)),
      catchError(() => of(null))
    );
  }

  /**
   * Persists tokens and user profile after a successful login or register.
   * @param sessionData - Access token, refresh token, and user from the API
   */
  private setSession(sessionData: { accessToken: string; refreshToken: string; user: User }): void {
    this.persistAccessToken(sessionData.accessToken);
    this.currentUser.set(sessionData.user);
    localStorage.setItem('refresh_token', sessionData.refreshToken);
    this.isInitialized.set(true);
  }

  /**
   * Clears in-memory auth state; optionally removes refresh token and redirects.
   * @param options - Clear behavior flags
   */
  private clearSession(options: ClearSessionOptions = {}): void {
    const removeRefresh = options.removeRefresh ?? true;
    const navigate = options.navigate ?? true;

    this.accessToken.set(null);
    this.currentUser.set(null);
    sessionStorage.removeItem('access_token');
    if (removeRefresh) {
      localStorage.removeItem('refresh_token');
    }
    this.isInitialized.set(true);
    this.isLoading.set(false);
    if (navigate) {
      this.router.navigate(['/auth/login']);
    }
  }

  /**
   * Stores the access token in memory and sessionStorage (survives HMR within the tab).
   * @param token - JWT access token
   */
  private persistAccessToken(token: string): void {
    this.accessToken.set(token);
    sessionStorage.setItem('access_token', token);
  }

  /** Hydrates access token from sessionStorage after a soft reload. */
  private restoreAccessTokenFromSession(): void {
    const token = sessionStorage.getItem('access_token');
    if (token) {
      this.accessToken.set(token);
    }
  }

  /**
   * True for network/API-down style failures that should not wipe the session.
   * @param err - Unknown error from HTTP layer
   */
  private isTransientFailure(err: unknown): boolean {
    if (!(err instanceof HttpErrorResponse)) {
      return true;
    }
    // status 0 = connection refused / CORS / API restarting
    if (err.status === 0) return true;
    if (err.status >= 500) return true;
    return false;
  }
}
